/**
 * Opens a draft in the user's own mail or messaging app — never sends.
 * Desktop browsers rarely have a working `mailto:` handler, so we open the
 * webmail compose window the user actually uses (Gmail / Outlook) and
 * remember that choice. Phones hand off to the native Mail / Messages apps.
 */
export type MailClient = 'gmail' | 'outlook' | 'app';
export const MAIL_CLIENT_LABEL: Record<MailClient, string> = { gmail: 'Gmail', outlook: 'Outlook', app: 'Mail app' };
const KEY = 'cfx.mailClient';

export type Draft = { to?: string[]; bcc?: string[]; subject: string; body: string };

export function isMobileDevice(ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''): boolean {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
}

/** Best guess when the user hasn't chosen: phones → native app; work-domain hints → Outlook; else Gmail. */
export function defaultMailClient(ua?: string, senderEmail?: string | null): MailClient {
  if (isMobileDevice(ua)) return 'app';
  if (senderEmail && /@(outlook|hotmail|live|msn)\./i.test(senderEmail)) return 'outlook';
  return 'gmail';
}

export function rememberedMailClient(): MailClient | null {
  try { const v = localStorage.getItem(KEY); return v === 'gmail' || v === 'outlook' || v === 'app' ? v : null; } catch { return null; }
}
export const MAIL_CLIENT_EVENT = 'cfx:mail-client';
export function rememberMailClient(c: MailClient) {
  try { localStorage.setItem(KEY, c); } catch { /* private mode */ }
  try { window.dispatchEvent(new CustomEvent(MAIL_CLIENT_EVENT, { detail: c })); } catch { /* SSR */ }
}

/** Button label for the chosen client — always names where the draft opens. */
export function emailButtonLabel(c: MailClient): string {
  return c === 'app' ? 'Email in Mail app' : `Email in ${MAIL_CLIENT_LABEL[c]}`;
}

/**
 * The ONE way an email draft is opened. Never silent: the full message is
 * always copied (a desktop `mailto:` with no handler does nothing) and the
 * caller gets a toast-ready description of what happened.
 */
export function dispatchEmail(c: MailClient, d: Draft): { title: string; description: string } {
  rememberMailClient(c);
  openDraft(composeUrl(c, d), `${d.subject}\n\n${d.body}`);
  return {
    title: `Opening ${MAIL_CLIENT_LABEL[c]}…`,
    description: c === 'app' && !isMobileDevice()
      ? 'Your message is copied. If nothing opens, choose Gmail or Outlook from the arrow.'
      : 'Your message is also copied, in case it doesn’t open.',
  };
}

const q = (o: Record<string, string | undefined>) =>
  Object.entries(o).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v!)}`).join('&');

export function composeUrl(client: MailClient, d: Draft): string {
  const to = (d.to ?? []).join(','); const bcc = (d.bcc ?? []).join(',');
  if (client === 'gmail') return `https://mail.google.com/mail/?view=cm&fs=1&${q({ to, bcc, su: d.subject, body: d.body })}`;
  if (client === 'outlook') return `https://outlook.office.com/mail/deeplink/compose?${q({ to, bcc, subject: d.subject, body: d.body })}`;
  const rest = q({ bcc, subject: d.subject, body: d.body });
  return `mailto:${encodeURIComponent(to).replace(/%2C/g, ',')}?${rest}`;
}

/** Phone hand-off: phones → Messages; desktop → WhatsApp Web (works without a phone app handler). */
export function phoneUrl(phone: string, body: string, ua?: string): { url: string; label: string } {
  if (isMobileDevice(ua)) return { url: `sms:${phone}?&body=${encodeURIComponent(body)}`, label: 'Text' };
  return { url: `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(body)}`, label: 'WhatsApp' };
}

/** The mail client to use right now: the user's remembered choice, else the device/domain best guess. */
export function preferredMailClient(senderEmail?: string | null, ua?: string): MailClient {
  return rememberedMailClient() ?? defaultMailClient(ua, senderEmail);
}

/** WhatsApp works everywhere (app on phones, Web on desktop); phone optional for "pick a chat". */
export function whatsappUrl(body: string, phone?: string | null): string {
  const d = phone ? phone.replace(/\D/g, '') : '';
  return `https://wa.me/${d}?text=${encodeURIComponent(body)}`;
}

/**
 * Native text message link — phones only. Desktop browsers have no reliable
 * `sms:` handler, so callers hide the Text action there (no dead ends).
 */
export function textUrl(body: string, phone?: string | null, ua?: string): string | null {
  if (!isMobileDevice(ua)) return null;
  return `sms:${phone ?? ''}?&body=${encodeURIComponent(body)}`;
}

/**
 * Opens the draft in a fresh top-level tab via a detached anchor click.
 * Gmail/Outlook/WhatsApp refuse to render inside frames (ERR_BLOCKED_BY_RESPONSE);
 * an anchor with target=_blank + noopener/noreferrer always escapes the embedding
 * frame and sends no referrer/opener. Native schemes (mailto:/sms:) go to the top
 * window so the OS handler fires even when the app is embedded.
 * Also copies `fallbackText` so the user is never stuck if a blocker stops the tab.
 */
export function openDraft(url: string, fallbackText?: string) {
  if (fallbackText) { try { void navigator.clipboard?.writeText(fallbackText); } catch { /* ignore */ } }
  if (url.startsWith('http')) {
    const a = document.createElement('a');
    a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a); a.click(); a.remove();
    return;
  }
  try { (window.top ?? window).location.href = url; } catch { window.location.href = url; }
}

/**
 * The device's own share sheet is only worth offering where it will actually
 * open. Browsers gate it behind the `web-share` permission, which an embedded
 * frame — a preview pane, any iframe — is normally denied, so the click would
 * silently do nothing. We only show the control when the app owns the tab.
 */
export function canNativeShare(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  try { if (window.self !== window.top) return false; } catch { return false; }
  return true;
}

/**
 * Hands the message to the device's share sheet. If the device refuses it, the
 * note lands on the clipboard instead — a share click is never silent.
 * `dismissed` means the person closed the sheet themselves: nothing to report.
 */
export async function shareOrCopy(text: string, title?: string): Promise<'shared' | 'copied' | 'failed' | 'dismissed'> {
  try {
    await navigator.share(title ? { title, text } : { text });
    return 'shared';
  } catch (err) {
    if ((err as DOMException | null)?.name === 'AbortError') return 'dismissed';
    try { await navigator.clipboard.writeText(text); return 'copied'; } catch { return 'failed'; }
  }
}

